import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import {
  setGenerationStats,
  resetGenerationStats,
  setGenerationLoading,
  setGenerationError,
} from "../../features/Generation/GenerationSlice";

export const GENERATION_COUNT_UPDATE_EVENT = "generation_count_update";

export const triggerGenerationCountRefresh = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(GENERATION_COUNT_UPDATE_EVENT));
  }
};

export default function useGenerationCount() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const {
    generationsLeft,
    iMaxAllowedGeneration,
    iGenerationCount,
    isLimitReached,
    loading,
    lastUpdated,
  } = useSelector((state) => state.generation || {});

  const refreshGenerationCount = useCallback(async () => {
    if (!token) {
      dispatch(resetGenerationStats());
      return null;
    }

    try {
      dispatch(setGenerationLoading(true));
      const response = await apiHandler({
        url: `${endpoint.API_BASE_URL}${endpoint.GET_GENERATION_COUNT}`,
        method: "GET",
        authToken: { token },
        noCache: true,
      });

      if (response?.status >= 200 && response?.status < 300) {
        const payload = response?.data?.data ?? response?.data ?? {};
        const rawGenerationsLeft =
          payload.generationsLeft !== undefined
            ? payload.generationsLeft
            : payload.iGenerationsLeft !== undefined
            ? payload.iGenerationsLeft
            : null;

        const rawMax =
          payload.iMaxAllowedGeneration !== undefined
            ? payload.iMaxAllowedGeneration
            : payload.maxAllowedGeneration ?? null;

        const rawCount =
          payload.iGenerationCount !== undefined
            ? payload.iGenerationCount
            : payload.generationCount ?? null;

        const parsedLeft =
          rawGenerationsLeft !== null && rawGenerationsLeft !== undefined
            ? Number(rawGenerationsLeft)
            : 0;

        const stats = {
          generationsLeft: Number.isFinite(parsedLeft) ? Math.max(0, parsedLeft) : 0,
          iMaxAllowedGeneration: rawMax !== null ? Number(rawMax) : null,
          iGenerationCount: rawCount !== null ? Number(rawCount) : null,
          isLimitReached:
            Boolean(payload.isLimitReached) ||
            (Number.isFinite(parsedLeft) && parsedLeft <= 0),
          iUserId: payload.iUserId ?? payload.userId ?? null,
        };

        dispatch(setGenerationStats(stats));
        return stats;
      } else {
        dispatch(setGenerationError("Failed to fetch generation count"));
        return null;
      }
    } catch (error) {
      console.error("Failed to fetch generation count:", error);
      dispatch(setGenerationError(error?.message || "Error fetching generation count"));
      return null;
    }
  }, [token, dispatch]);

  useEffect(() => {
    refreshGenerationCount();
  }, [refreshGenerationCount]);

  useEffect(() => {
    if (!token) return undefined;

    // Periodic polling every 30 seconds
    const intervalId = setInterval(() => {
      refreshGenerationCount();
    }, 30000);

    // Event listener for manual / event-driven refreshes
    const handleCustomUpdate = () => {
      refreshGenerationCount();
    };
    window.addEventListener(GENERATION_COUNT_UPDATE_EVENT, handleCustomUpdate);

    // Refetch when tab gains visibility or focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshGenerationCount();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleCustomUpdate);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener(GENERATION_COUNT_UPDATE_EVENT, handleCustomUpdate);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleCustomUpdate);
    };
  }, [token, refreshGenerationCount]);

  return {
    generationsLeft,
    iMaxAllowedGeneration,
    iGenerationCount,
    isLimitReached,
    loading,
    lastUpdated,
    refreshGenerationCount,
  };
}
